import { PostsDataMap, PostsDataArray } from './PostsData';

// Recursive category structure - subcategories can have their own subcategories
const categories = {
    argentina: {
        id: 'argentina',
        name: {
            en: 'Argentina',
            es: 'Argentina',
            de: 'Argentina',
            it: 'Argentina',
            fr: 'Argentina',
        },
        subcategories: {
            'buenos-aires': {
                id: 'buenos-aires',
                name: {
                    en: 'Buenos Aires',
                    es: 'Buenos Aires',
                    de: 'Buenos Aires',
                    it: 'Buenos Aires',
                    fr: 'Buenos Aires',
                },
                subcategories: {
                    barrios: {
                        id: 'barrios',
                        name: {
                            en: 'Neighborhoods',
                            es: 'Barrios',
                            de: 'Stadtteile',
                            it: 'Quartieri',
                            fr: 'Quartiers',
                        },
                    },
                    actividades: {
                        id: 'actividades',
                        name: {
                            en: 'Activities',
                            es: 'Actividades',
                            de: 'Aktivitäten',
                            it: 'Attività',
                            fr: 'Activités',
                        },
                    },
                    cafes: {
                        id: 'cafes',
                        name: {
                            en: 'Cafes',
                            es: 'Cafés',
                            de: 'Cafés',
                            it: 'Cafés',
                            fr: 'Cafés',
                        },
                    },
                },
            },
        },
    },
    travel: {
        id: 'travel',
        name: {
            en: 'Travel',
            es: 'Viajes',
            de: 'Reisen',
            it: 'Viaggi',
            fr: 'Voyage',
        },
        subcategories: {
            escapadas: {
                id: 'escapadas',
                name: {
                    en: 'Getaways',
                    es: 'Escapadas',
                    de: 'Kurztrips',
                    it: 'Fughe',
                    fr: 'Escapades',
                },
            },
        },
    },
};

// Helper function to get category by path (array of IDs)
const getCategoryByPath = (path) => {
    if (!path || path.length === 0) return null;

    let current = categories[path[0]];
    if (!current) return null;

    for (let i = 1; i < path.length; i++) {
        if (!current.subcategories || !current.subcategories[path[i]]) {
            return null;
        }
        current = current.subcategories[path[i]];
    }

    return current;
};

// Helper function to get category name by path and language
const getCategoryNameByPath = (path, language) => {
    const category = getCategoryByPath(path);
    if (!category) return path.join(' > ');
    return category.name[language] || category.name['en'];
};

// Helper function to find the full path to a category by searching the tree
const findCategoryPath = (
    categoryId,
    currentPath = [],
    currentCategories = categories
) => {
    // Check if current category matches
    if (currentCategories[categoryId]) {
        return [...currentPath, categoryId];
    }

    // Search in subcategories
    for (const [key, category] of Object.entries(currentCategories)) {
        if (
            category.subcategories &&
            Object.keys(category.subcategories).length > 0
        ) {
            const found = findCategoryPath(
                categoryId,
                [...currentPath, key],
                category.subcategories
            );
            if (found) {
                return found;
            }
        }
    }

    return null;
};

// Helper function to get full category path from a leaf category ID
const getCategoryPathFromLeaf = (leafCategoryId) => {
    return findCategoryPath(leafCategoryId) || [leafCategoryId];
};

// Helper function to get post image with default fallback
const getPostImage = (customImage) => {
    if (customImage) {
        // If custom image is provided, prepend public URL and /img/ prefix
        return `${process.env.PUBLIC_URL || ''}/img/posts/${customImage}`;
    }
    return `${process.env.PUBLIC_URL || ''}/img/base_post_image.png`;
};

// Helper function to get post content based on language
const getPostContent = (postKey, language) => {
    const lang = language || 'en';
    return (
        PostsDataMap[postKey]?.content?.[lang] ||
        PostsDataMap[postKey]?.content?.['en']
    );
};

// Function to get posts with current language
const getPosts = (language) => {
    const lang = language || 'en';
    return PostsDataArray.map((post) => ({
        title: getPostTitle(post.id, lang),
        date: getPostDate(post.id, lang),
        description: getPostDescription(post.id, lang),
        image: getPostImage(post.image),
        content: getPostContent(post.id, lang),
        route: post.route,
        categories: post.categories,
        featured: post.featured,
    }));
};

// Helper functions for titles, dates, and descriptions
const getPostTitle = (postKey, language) => {
    return (
        PostsDataMap[postKey]?.title?.[language] ||
        PostsDataMap[postKey]?.title?.['en']
    );
};

const getPostDate = (postKey, language) => {
    return (
        PostsDataMap[postKey]?.date?.[language] ||
        PostsDataMap[postKey]?.date?.['en']
    );
};

const getPostDescription = (postKey, language) => {
    return (
        PostsDataMap[postKey]?.description?.[language] ||
        PostsDataMap[postKey]?.description?.['en']
    );
};

// Helper function to get featured posts
const getFeaturedPosts = (language) => {
    const allPosts = getPosts(language);
    return allPosts.filter((post) => post.featured === true);
};

// Export default posts (will be updated dynamically)
const Posts = getPosts('en');

export default Posts;
export {
    getPosts,
    getPostContent,
    getFeaturedPosts,
    categories,
    getCategoryByPath,
    getCategoryNameByPath,
    getCategoryPathFromLeaf,
};
